// Single source of truth for the 15 product modules (EOS-01..15).
// Home page and sidebar navigation both render from this list, so no module
// can silently disappear from the product. Status mirrors docs/project-status.md.

export type ModuleStatus = "LIVE" | "BACKEND_ONLY" | "PLANNED";

// The icon of a module (Master Prompt A25 (e)): a Lucide icon name, rendered by
// components/ModuleIcon.tsx beside the module's name, never instead of it.
export type ModuleIconName =
  | "FolderKanban"
  | "Gauge"
  | "Zap"
  | "Activity"
  | "ShieldCheck"
  | "Cable"
  | "LayoutPanelTop"
  | "ArrowDownToLine"
  | "CloudLightning"
  | "ShieldAlert"
  | "Waves"
  | "Route"
  | "FileText"
  | "ClipboardCheck"
  | "Radio";

export type ModuleDefinition = {
  code: string;
  name: string;
  summary: string;
  status: ModuleStatus;
  icon: ModuleIconName;
  // A route exists only when the page works; PLANNED and BACKEND_ONLY have none.
  route: string | null;
  // Further pages of the same module, matched exactly: the sidebar link (which goes to
  // route) is shown as the current one on each of them too.
  activeOn?: readonly string[];
};

export const MODULE_STATUS_LABELS: Record<ModuleStatus, string> = {
  LIVE: "Live",
  BACKEND_ONLY: "Backend only",
  PLANNED: "Planned",
};

export const MODULES: readonly ModuleDefinition[] = [
  {
    code: "EOS-01",
    icon: "FolderKanban",
    name: "Project configuration",
    summary:
      "Project identity, jurisdiction profile, units and applicable standards, and the " +
      "project revision's single-line diagram.",
    status: "LIVE",
    route: "/projects",
    // The single-line diagram (SLD-a, A26) is the module's second page; the project
    // configuration switch moves between them.
    activeOn: ["/sld"],
  },
  {
    code: "EOS-02",
    icon: "Gauge",
    name: "Load and demand",
    summary: "Connected load, diversity and maximum demand estimation.",
    status: "LIVE",
    route: "/load-demand",
  },
  {
    code: "EOS-03",
    icon: "Zap",
    name: "Transformer, DG, UPS and PV",
    summary:
      "Source sizing for transformers, generators, UPS and PV systems. " +
      "Transformer, DG, UPS and PV sizing are live.",
    status: "LIVE",
    route: "/transformer-sizing",
    // The DG, UPS and PV pages belong to the same module; the source sizing switch
    // moves between them.
    activeOn: ["/generator-sizing", "/ups-sizing", "/pv-sizing"],
  },
  {
    code: "EOS-04",
    icon: "Activity",
    name: "Short-circuit study",
    summary: "Fault currents at a bus with sequence impedances and source contributions.",
    status: "LIVE",
    route: "/fault-study",
  },
  {
    code: "EOS-05",
    icon: "ShieldCheck",
    name: "Protection coordination",
    summary:
      "Relay and device settings, discrimination and coordination checks. " +
      "Type 1 / Type 2 starter coordination, LV breaker duty and selectivity evidence checks " +
      "and the earth-fault loop impedance and disconnection check are live; HT relay grading " +
      "follows.",
    status: "LIVE",
    // EOS-05 is delivered in parts (A20, A21, A33): 05a starter coordination is the first
    // page; the protection switch moves to the 05b breaker duty and selectivity page and the
    // 05d earth-fault loop page.
    route: "/starter-coordination",
    activeOn: ["/breaker-selectivity", "/earth-fault-loop"],
  },
  {
    code: "EOS-06",
    icon: "Cable",
    name: "Cable sizing",
    summary: "LV cable selection with ampacity, voltage-drop and short-circuit checks.",
    status: "LIVE",
    route: "/cable-sizing",
  },
  {
    code: "EOS-07",
    icon: "LayoutPanelTop",
    name: "Panels and switchboards",
    summary:
      "Assembly checks against IEC 61439 ratings. Two parts are live: busbar selection by " +
      "current and material, and the busbar evidence check against the builder's " +
      "design-verified ratings (panel busbars and busbar trunking). HT switchgear busbars " +
      "and the other assembly checks follow.",
    status: "LIVE",
    // EOS-07 is delivered in parts (A24, A29): one route with two modes behind `?mode=`,
    // busbar selection (07c) and the builder's evidence check (07a); 07b and the rest follow.
    route: "/busbar",
  },
  {
    code: "EOS-08",
    icon: "ArrowDownToLine",
    name: "Earthing",
    summary:
      "Protective conductor thermal check and earth electrode resistance; grid touch and step " +
      "voltage in V1.1",
    status: "LIVE",
    // EOS-08 is delivered in parts (A36): 08a, the protective conductor thermal check and the
    // electrode resistance, on one route; the grid study follows in V1.1.
    route: "/earthing",
  },
  {
    code: "EOS-09",
    icon: "CloudLightning",
    name: "Lightning protection",
    summary: "Risk assessment and air-termination layout by the rolling-sphere method.",
    status: "PLANNED",
    route: null,
  },
  {
    code: "EOS-10",
    icon: "ShieldAlert",
    name: "Surge protection",
    summary:
      "The SPD of one location against declared figures: the effective protection level (Up plus " +
      "the lead drop) against each protected item's rated impulse withstand, Uc and the discharge " +
      "parameters against declared requirements, the short-circuit rating against the linked fault " +
      "level. Whether an SPD is required and the lightning zones follow.",
    status: "LIVE",
    route: "/surge-protection",
  },
  {
    code: "EOS-11",
    icon: "Waves",
    name: "Power factor and harmonics",
    summary:
      "Compensation sizing and harmonic distortion checks. Two parts are live: power factor " +
      "correction of a bus with the capacitor bank, a detuned reactor and the resonance " +
      "screening, and the harmonic assessment at the point of common coupling (THDi, TDD, " +
      "THDv, Isc / IL against a declared limit).",
    status: "LIVE",
    // EOS-11 is delivered in two parts (A32): one route with two modes behind `?mode=`.
    route: "/power-quality",
  },
  {
    code: "EOS-12",
    icon: "Route",
    name: "Cable tray and routing",
    summary:
      "Tray fill from the declared cable diameters (area of a bunched layer, width of a single " +
      "layer), the route load against the declared rating at its span and support spacing, and " +
      "the installation-method consistency of the linked cable studies. Segregation and covers " +
      "follow.",
    status: "LIVE",
    route: "/cable-tray",
  },
  {
    code: "EOS-13",
    icon: "FileText",
    name: "Deliverables",
    summary: "Printable calculation reports of stored runs; schedules, BOQ and transmittals follow.",
    status: "LIVE",
    route: "/reports",
    // The report pages (A31): a run by id, or a study by key; matched as route patterns.
    activeOn: ["/reports/runs/:runId", "/reports/by-key"],
  },
  {
    code: "EOS-14",
    icon: "ClipboardCheck",
    name: "FAT and SAT",
    summary: "Factory and site acceptance test calculators and records.",
    status: "PLANNED",
    route: null,
  },
  {
    code: "EOS-15",
    icon: "Radio",
    name: "Metering, BMS and SCADA",
    summary: "Metering points, signal lists and supervisory interfaces.",
    status: "PLANNED",
    route: null,
  },
];

export function getModule(code: string): ModuleDefinition | undefined {
  return MODULES.find((module) => module.code === code);
}

export function liveModules(): ModuleDefinition[] {
  return MODULES.filter((module) => module.status === "LIVE");
}
